import { DataInterfaceComparer } from "Global/Base/DataSources/DataInterfaceComparer";
import { DIContainer } from "Global/Base/DIContainer";
import { PromiseUtils } from "Global/Base/Promises/PromiseUtils";
import { RightType } from "Global/Base/Rights/RightTypes";
import { IWorkflowManager, IWorkflowMenuItem } from "OW.Remoting/Remoting/IWorkflowManager";
import { ErrorHandler } from "UI/Errors";
import { ControlRightTypesGroup, RightGroups } from "UI/UI/Rights/RightGroups";
import { Event, IListener } from "../../../../Global/Base/Events";
import { EOWUserException, EOWValidateException } from "../../../../OW.Remoting/Basic/OWExceptions";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { IValidatableContainer } from "../../../UI.BaseControls/ControlInterfaces";
import { ActionType } from "../../../UI.Controls/Actions/IAction";
import { SaveResultData } from "../../../UI.Controls/DataSource/EditableDBDataSource";
import { FormControlParams, IVersioned } from "../Controls/ControlInterfaces";
import { UIManager } from "../UIManager";
import { OpenModeType } from "./Actions/DetailFormAction";
import { WFAction } from "./Actions/WFAction";
import { DetailFrame } from "./DetailFrame";
import { IValidationMaster } from "./FormControl";
import { FrameSelChanged, IActualObjectChangedEventArgs } from "./Frames";
import { IMasterableFrame } from "./Frames/FrameInterfaces";
import { FrameSharedMethods } from "./Frames/FrameSharedMethods";
import { ISaveable } from "./Frames/IDetailMasterableFrame";
import { MasterableFrame } from "./Frames/MasterableFrame";
import { IForm } from "./IForm";
import { IFrame } from "./IFrame";
import { SharedMethods } from "./SharedMethods";
import { ValidationManager } from "./ValidationManager";

/** Samostatný detail, otevíraný do nového okna, který nemá nikdy mastera a pracuje pouze s jedním konkrétním objektem. Sám si řeší ukládání, validace i rušení změn */
export class WindowedDetailFrame extends DetailFrame implements IValidatableContainer, IValidationMaster, ISaveable, IMasterableFrame, IVersioned {
    //#region Fields

    /**
     * Interní kolekce Workflow položek aktuálního objektu. Ke kolekci je potřeba přistupovat přes property workflowMenuItems.
     */
    private __workflowMenuItems: IWorkflowMenuItem[];

    protected _previousWorkflowMenuItems: IWorkflowMenuItem[];

    private _readVersioned: boolean | null;

    //#endregion Fields

    //#region Events
    public readonly selectionChanged: Event<IMasterableFrame, FrameSelChanged> = new Event<IMasterableFrame, FrameSelChanged>();
    public override readonly actualObjectSaved: Event<IMasterableFrame, OWObject> = new Event<IMasterableFrame, OWObject>();
    public readonly workflowItemsChanging: Event<WindowedDetailFrame, IWorkflowMenuItem[]> = new Event<WindowedDetailFrame, IWorkflowMenuItem[]>();
    public readonly workflowItemsChanged: Event<WindowedDetailFrame, IWorkflowMenuItem[]> = new Event<WindowedDetailFrame, IWorkflowMenuItem[]>();
    //#endregion Events

    //#region Listeners
    private _detailFormMasterFrameObjectChangedListener: IListener;
    private _contextCreatedListener: IListener | undefined;

    //#endregion Listeners

    //#region Properties
    public readonly canBeMasterFrame: boolean = true;
    private readonly _workflowManager: IWorkflowManager;

    /** Obsahuje aktuální validační chyby */
    public get validationManager(): ValidationManager {
        return this._validationManager;
    }

    /** get - Prázdné, nemá mastera */
    public override get masterFrameName(): string {
        if (this.ownerForm.multiEditMode) return super.masterFrameName;
        return ""; // nikdy nemá mastera
    }

    /** set - Neprovede nic, nepoužívá mastera */
    public override set masterFrameName(v: string) {
        if (this.ownerForm.multiEditMode) super.masterFrameName = v;
        // nikdy nemá mastera
    }

    public override get readVersioned(): boolean | null {
        return this._readVersioned;
    }

    public override get actualObject(): OWObject {
        return super.actualObject;
    }

    public override set actualObject(obj: OWObject) {
        super.actualObject = obj;
        if (this.actualObject) { // Při aktualizaci objektu je třeba aktualizovat data na provázaných komponentách
            this.actualObject.dataRefreshed.add(() => {
                this.actualObjectChanged.trigger(this, { actualObject: this.actualObject, previousActualObject: this.actualObject });
                this._refreshDisplayedData();
            });
            this._openDetailObjectChanged(this.ownerForm.detailFormObject);
        }
    }

    public override get isMultiSelecting(): boolean {
        return this.ownerForm?.multiEditMode;
    }

    /**
     * Formulářová práva v detailovém okně chceme vyhodnocovat vždy
     */
    protected override get _usesFormRights(): boolean {
        return true;
    }

    /** Zda má být potlačen ukládací dialog */
    public override get suppressSaveDialog(): boolean {
        return this._suppressSaveDialog;
    }

    /**
     * Položky Workflow aktuálního objektu
     */
    protected set _workflowMenuItems(items: IWorkflowMenuItem[]) {
        this.__workflowMenuItems = items;
        this.workflowItemsChanging.trigger(this, items);
    }

    protected get _workflowMenuItems(): IWorkflowMenuItem[] {
        return this.__workflowMenuItems;
    }

    //#endregion Properties

    //#region Constructors
    constructor(params: FormControlParams) {
        super(params);

        this._readVersioned = params.ownerForm.readVersioned;
        this._workflowManager = DIContainer.instance.create(IWorkflowManager);
        /**
         * V konstruktoru framu může být jen jednoduchá inicializace hodnot (z definice a pod.)
         * Logika a asynchronní procesy musí být až v inicializační metodě (initialize()),
         * která je vyvolávaná v potřebném pořadí a dá se na její doběhnutí čekat
         */
        this.hasSplitter = false;

        this._copyActionsFromMasterFrame();
        // Nemá masterFrame, proto je nutné přehodit useMasterContext
        this.useMasterContext = false;
    }
    //#endregion Constructors

    //#region Methods

    /**
     * Řeší přidání akcí na DetailFrame ze skrytého nadřízeného master framu
     * @see https://tfs/Jadro/Jadro%20.NET/_workitems/edit/27303
     */
    private _copyActionsFromMasterFrame(): void {
        let masterFrameElement: HTMLElement;
        const hiddenMasterFrameName = this.getDataAttr("masterframe");
        if (hiddenMasterFrameName) // Neměly by se vzít akce z master framu i z detailFormMasterFrame?
            masterFrameElement = this.ownerForm.element.querySelector(`[data-behaviour='frame'][data-framename='${hiddenMasterFrameName}']`) || this.ownerForm.detailFormMasterFrame.element;

        const transferedActions = Array.from(masterFrameElement.querySelectorAll("[data-behaviour='action']")).map((actionElement) => actionElement.cloneNode(true));

        const actionsDetailFrame = this.element.querySelector("[data-tsccontrol='DetailFrame'] > [data-tscitem='Actions']"); // Vybírá kontejner akcí na detailFrame
        if (!actionsDetailFrame) {
            const actionsSpan = document.createElement("span");
            actionsSpan.setAttribute("data-tscitem", "Actions");
            actionsSpan.append(...transferedActions);
            this.element.appendChild(actionsSpan);
        } else
            actionsDetailFrame.append(...transferedActions); // Akce z masterFrame přidáme k akcím na detailFrame
    }

    /** Validuje podřízené framy */
    protected async _validateValidatableFrames(): Promise<boolean> { // Na předkovi neexistuje, v jeho případě to řeší master
        const results = [];
        for (const aktFrame of this._validatableFrames) {
            results.push(aktFrame.validateAndShowMessage());
        }
        return (await Promise.all(results)).every(result => result);
    }

    /** Validuje tento frame */
    public override async validate(): Promise<boolean> { // Nepoužívá se implementace předka, jelikož ta spoléhá na mastera
        await this.waitForPendingActions();
        await this.checkEmptyComponents(this.validateRecommended);

        const frameValidation = await this._validateValidatableFrames();

        if (this._validationManager.hasErrors || !frameValidation)
            return false;

        try {
            await OWManager.validateObject(this.actualObject.instanceID, this._getRequestContext());
            return true;
        } catch (error) {
            if (error instanceof EOWValidateException) {
                this._handleSaveErrors([{ data: this.actualObject, errors: error.validationErrors }]);
            } else {
                ErrorHandler.instance.handleError(error);
            }

            return false;
        }
    }

    /** Aktualizuje ActualObject na základě kliku na akci refreshe v toolbaru */
    protected override async _refreshClick(): Promise<void> {
        await this.actualObject.reload();
        await this._refreshDisplayedData();
    }

    /** Uloží detailový objekt na základě akce od uživatele */
    public override async saveAllClick(): Promise<boolean> { // Předek řeší ukládání přes mastera a to tady nejde použít
        try {
            if (this.ownerForm.multiEditMode) {
                return await this.saveRecords(null, this.validateRecommended) === null;
            }
            return await this.save(false) == null;
        } catch (error) {
            ErrorHandler.instance.handleError(error);

            return false;
        }
    }

    /** Uloží detailový objekt a zobrazí případné chyby */
    public async save(skipSavingEditContextId?: boolean): Promise<SaveResultData[] | null> {
        let result: SaveResultData[] = null;

        UIManager.instance.showLoader();

        const savingWithoutChanges = !this.actualObject.isChanged;
        try {
            await this.waitForPendingUpdates();
            await this._addValidationErrosForLoadedAttrs();
            await this.actualObject.waitForUpdateInstance();

            if (!skipSavingEditContextId && await this.validate()) {
                const messages = await this.actualObject.applyUpdates();

                if (messages.length > 0) {
                    result = [{ data: this.actualObject, messages: messages }];
                }
            }

            if (!skipSavingEditContextId && this.isEditContextInitiator) {
                if (await this.ownerForm.checkAndSaveForm({ skipSavingEditContextId: true, forceSave: true, sourceFrame: this })) {
                    await this.saveContext();
                }
            }

            // Není třeba spouštět refresh dat
            if (!this.isEditContextInitiator) {
                this.actualObjectSaved.trigger(this, this.actualObject);
            }
        } catch (error) {
            if (error instanceof EOWValidateException) {
                result = [{ data: this.actualObject, errors: error.validationErrors }];
            } else if (error instanceof EOWUserException) {
                result = [{ data: this.actualObject, messages: error.resultMessages }];
            } else {
                throw error;
            }
        } finally {
            if (!skipSavingEditContextId && this.validationManager.hasErrors) { // Neprošlo to validací ještě před uložením
                result = [{ data: this.actualObject, errors: this.actualObject.getValidationErrors().map((error) => error.error) }];
            } else if (result) {
                this._handleSaveErrors(result); // Vloží chyby do validationManager
            }

            if (result?.length > 0) {
                if (result[0].errors && result[0].errors.length > 0) {
                    SharedMethods.showValidationMessages(this.validationManager); // U chyb se o jejich zobrazení stará tato metoda
                } else {
                    result = await SharedMethods.processSaveResultsAndShowMessages(this, result); // U zpráv se o jejich zobrazení stará naopak tato metoda
                }
            } else {
                await this.setDetailToEditMode(this.isEditContextInitiator, this.interactive);

                // Mohlo dojít k meziuložení a tak nemusela být žádná změna, ale to je taky v pořádku
                if (this.isEditContextInitiator || skipSavingEditContextId) {
                    result = null;
                }
            }

            UIManager.instance.hideLoader();

            if (savingWithoutChanges)
                this.unlockObject();

            // eslint-disable-next-line no-unsafe-finally
            return result;
        }
    }

    /**
     * Zahájí editaci záznamu.
     */
    public override async editRecord(): Promise<boolean> {
        // Pro nový objekt na tomto framu nemá smysl kontrolovat potřebu uložení formuláře, proto vstoupíme rovnou do editace.
        if (this.actualObject?.isNew) {
            if (!await this.lockObject(this.actualObject)) {
                return false;
            }
            this.editingRow.trigger(this, this.actualObject);
            await this.setDetailToEditMode(true, true);
            return true;
        } else {
            return super.editRecord();
        }
    }

    /**
     * Navratove hodnoty:
     * null - ulozeni probehlo v poradku
     * prazdne pole - ulozeni se nepodarilo, ale neni co zpracovat
     * naplene pole - vraci vyjimky, vznikle pri ukladani
     * @param id Pokud je vyplněn, uloží pouze jeden záznam. V opačném případě ukládá všechny rozeditované objekty.
     * @param checkRecommended Pokud je vyplněno, tak validuje i doporučená pole
     * @param checkPostponed Pokud je vyplněno, tak validuje i povinně odložená pole
     */
    public override async saveRecords(_key?: string, _checkRecommended?: boolean, _checkPostponed?: boolean, _skipValidation?: boolean, _isFormClosing?: boolean, skipSavingEditContextId: boolean = false): Promise<SaveResultData[] | null> {
        if (this.ownerForm.multiEditMode) {
            return super.saveRecords(_key, _checkRecommended, _checkPostponed, _skipValidation, _isFormClosing, skipSavingEditContextId);
        } else {
            return this.save(skipSavingEditContextId);
        }
    }

    /** Zruší změny na objektu */
    public override async cancelChanges(): Promise<void> {
        await this.unlockObject();

        if (this.isEditContextInitiator) {
            if (await this.ownerForm.checkAndSaveForm({ forceSave: true, skipSavingEditContextId: true, cancelChanges: true, sourceFrame: this })) {
                await this.rollbackContext();
            }
        }

        if (!this.isEditContextInitiator) {
            this.isEditing = false;
            this.isChanged = false;
        }

        // Zrušení změn na novém objektu nejde, protože neexistuje stav, ke kterému se vrátit a při pouhém zahození objektu by zůstal prázdný nepoužitelný form, proto se rovnou i zavře.
        if (this.actualObject.isNew) {
            this._setActualObject(null);
            this.changesCanceled.trigger(this, undefined);
            this.ownerForm.close();
        } else {
            await this.actualObject.reload();
            await this._refreshDisplayedData();

            await this.setDetailToEditMode(false, this.interactive);
            this.changesCanceled.trigger(this, undefined);
        }
    }

    private async _setActualObjectInternal(id: any, objectName: string): Promise<void> {
        if (this.ownerForm.multiEditMode) { // pokud jsme v multieditacnim rezimu je potreba pouzit MEOWObjekt z mastera
            this._setActualObject(this.ownerForm.detailFormObject);
        } else {
            /* getObject řeší situaci, kdy je Form otevřen do nového Frame/Window a je tak nutné načíst nové instance objektů (na přenesených není funkční např.instanceof viz.:
                https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/instanceof#instanceof_and_multiple_context_(e.g._frames_or_windows) */
            if (this.ownerForm.openMode === OpenModeType.insert) { // Pokud je objekt isNew, tak v objectId je instanceID místo ID
                this._setActualObject(await this._newOrCopyObject());
            } else {
                this._setActualObject(await OWManager.getObject(id, objectName, undefined, undefined, this.ownerForm.readVersioned, this._getRequestContext()));
            }
        }
    }

    /**
     * Založí nový objekt, nebo kopii existujícího.
     */
    private _newOrCopyObject(): Promise<OWObject> {
        if (this.ownerForm.selectedId) { // V selectedId je instanceId kopírovaného objektu.
            return OWManager.createCopyObject(this.ownerForm.selectedId);
        } else {
            return OWManager.newObject(this.objectName);
        }
    }

    /** Místo mastera v případě předka inicializuje konkrétní objekt používaný v rámci tohoto detailu */
    protected override async _initializeDetail(): Promise<void> {
        // V případě MSLOV vůbec nechodí openDetailObject, ale jen masterObject, v případě insertModu nechodí ani jedno z toho
        const objectId = this.ownerForm.detailFormObject ? this.ownerForm.detailFormObject.id : (this.ownerForm.masterObject ? this.ownerForm.masterObject.id : "");
        const objectName = this.ownerForm.detailFormObject ? this.ownerForm.detailFormObject.objectName : (this.ownerForm.masterObject ? this.ownerForm.masterObject.objectName : "");
        await this._setActualObjectInternal(objectId, objectName);
    }

    /** Zaregistruje události, na které chceme reagovat */
    protected override _initializeListeners(): void {
        super._initializeListeners();

        this.ownerForm.detailFormMasterFrame?.actualObjectChanged.add(this._detailFormMasterFrameObjectChangedListener = (_sender: IFrame, eventArgs: IActualObjectChangedEventArgs) => this._detailFormMasterFrameObjectChanged(eventArgs.actualObject));

        this.actualObjectSaved.add(async (_sender, actObj) => {
            await this._getWFItems(actObj);
        });
    }

    /** Vytvoří menu a vloží do něj akce potřebné pro jeden konkrétní objekt */
    protected override async _initializeFormUI(): Promise<void> {
        this.menu = this.ownerForm.formUI.createFrameMenu(this);

        await this._initializeDom();
        //this.evaluateConditions(); // Musí být přes this._actualizeRights, který čeká na uvnitř vytvořený promise // TODO-rites: old rights, remove
        await this._actualizeRights();
        this._createRefreshAction(this.hideButtons.refresh);
        this._createEditAction(this.hideButtons.edit);
        await this._addDefaultActionToMenu(this.hideButtons);
        this._createCustomActions();

        const wfItemsResult = await this._workflowManager.getWFItems(this.actualObject, true, this._getRequestContext()).catch((err) => {
            ErrorHandler.instance.handleError(err);
            return null;
        });

        if (wfItemsResult) {
            await this._initializeWorkflowActions(wfItemsResult.workflowMenuItems, this, this.ownerForm, this.ownerForm.readVersioned);
        }

        if (this._allowReport) {
            this._createReportAction();
        }

        this._prepareButtons(this);

        //await this._checkChildControlsRights(this.startOfDomStructure); // TODO-rites: old rights, remove

        this._registerFormUIControls();

        await this._loadDataForDetail();

        if (this.ownerForm.visibleOnlyDetail)
            this.menu.setVisibility(this.canValueLoad);

        this._contextCreatedListener = async (_sender, value) => {
            await this.setDetailToEditMode(value, false);
        };

        this.contextCreated.add(this._contextCreatedListener);

        if (this.ownerForm.multiEditMode) // pokud je otevren jen detail v multieditaci, je potreba jeste jedno nacist prava na multieditaci, aby doslo ke spravnemu prepnuti mixinu
            self.setTimeout(() => this._newEvaluateRights([RightType.multiEditing]));
    }

    protected override async _initializeWorkflowActions(wfItems: IWorkflowMenuItem[], masterFrame: IMasterableFrame, ownerForm: IForm, readVersioned: boolean = false): Promise<void> {
        super._initializeWorkflowActions(wfItems, masterFrame, ownerForm, readVersioned);
        this._workflowMenuItems = wfItems;

        this.workflowItemsChanging.add(async (masterableFrame, workflowMenuItems) => {
            FrameSharedMethods.clearWorkflowItems(this._wfActions, this.menu);
            if (!this.hideButtons.workflow) {
                this._wfActions = await FrameSharedMethods.createWorkflowItems(workflowMenuItems, masterableFrame, this.ownerForm) as Set<WFAction>;
                this._wfActions.forEach((a) => this.menu.addAction(a));
            }
            this.workflowItemsChanged.trigger(this, workflowMenuItems);
        });
    }

    protected override _createEditAction(hbEdit: boolean): void {
        super._createEditAction(hbEdit);

        if (this.ownerForm.multiEditMode) {
            const action = FrameSharedMethods.createMultiSelectAction(ActionType.standardEditRecords, async () => {
                await this.setDetailToEditMode(true, true);
            }, this);
            this.menu.addAction(action);
        }
    }

    /**
     * Handler události změny objektu na frame, který tento frame nahrazuje v detailovém formuláři bez seznamu.
     * @param newObj Nový objekt.
     */
    private async _detailFormMasterFrameObjectChanged(newObj: OWObject): Promise<void> {
        if (this.actualObject.id === newObj?.id) {
            await this._setActualObjectInternal(newObj.id, newObj.objectName);
        }
    }

    public override async refreshActualObject(): Promise<void> {
        if (this.actualObject == null)
            return;

        this._validationManager.clearErrors(this.actualObject.id);

        await this._setActualObjectInternal(this.actualObject.id, this.actualObject.objectName);
        await this._refreshDisplayedData();
    }

    private _openDetailObject: OWObject | null = null;

    private _openDetailObjectRemoveObjectListener: IListener;

    private _openDetailObjectSaveObjectListener: IListener;

    private _openDetailObjectDataRefreshObjectListener: IListener;

    private _openDetailObjectDisposeListeners(obj: OWObject): void {
        obj.deleted.remove(this._openDetailObjectRemoveObjectListener);
        obj.saved.remove(this._openDetailObjectSaveObjectListener);
        obj.dataRefreshed.remove(this._openDetailObjectDataRefreshObjectListener);
    }
    /**
     * Při změně objektu aktualizuje listenery na změny u stejného záznamu v seznamu
     * @param newObj Nově vybraný objekt
     */
    private _openDetailObjectChanged(newObj: OWObject): void {
        if (newObj == null ||
            newObj.id !== this.ownerForm.detailFormObject.id) {
            return;
        }
        //pokud se změnil _openDetailObject, tak odebereme listenery přidané v této metodě
        if (this._openDetailObject) {
            this._openDetailObjectDisposeListeners(this._openDetailObject);
        }
        this._openDetailObject = newObj;

        // Pokud uživatel smaže v původním okně záznam zobrazený v tomto detailu, tak je třeba celý form zavřít
        newObj.deleted.add(this._openDetailObjectRemoveObjectListener = () => this.ownerForm.close());

        // Pokud uživatel v původním okně uloží zobrazený záznam, tak aktualizujeme zobrazená data
        newObj.saved.add(this._openDetailObjectSaveObjectListener = () => this.actualObject.reload());

        // Pokud je změněn stav pomocí workflow, aktualizujeme data (event saved není vyvolán)
        newObj.dataRefreshed.add(this._openDetailObjectDataRefreshObjectListener = async () => {
            const canReloadObject = !this.actualObject.isChanged;
            if (canReloadObject) {
                // odstraníme dočasně event listener, aby se zabránilo opětovné aktualizaci na seznamu
                this.actualObject.dataRefreshed.remove(this._openDetailObjectDataRefreshObjectListener);
                await this.actualObject.reload();
            }
            await this.reloadWFItems();
            if (canReloadObject) {
                if (!this.actualObject.dataRefreshed.containsListener(this._openDetailObjectDataRefreshObjectListener))
                    this.actualObject.dataRefreshed.add(this._openDetailObjectDataRefreshObjectListener);
            }
        });

        // Pokud uživatel v tomto okně uloží zobrazený záznam, tak aktualizujeme data na objektu v seznamu (pokud je vybraný)
        this.actualObject.saved.removeAll();
        this.actualObject.saved.add(() => newObj.reload());
        if (!this.actualObject.dataRefreshed.containsListener(this._openDetailObjectDataRefreshObjectListener)) {
            this.actualObject.dataRefreshed.add(this._openDetailObjectDataRefreshObjectListener);
        }

        this.ownerForm.closed.add(() => { // Jinak zůstane objekt celého detailu viset v paměti, dokud není zrušen i provázaný OWObjekt ze seznamu
            this._openDetailObjectDisposeListeners(newObj);
        });
    }
    /**
     * Metoda spouští vyhodnocení práv při změně atributu.
     * Měla by být debounced, v některých případech se můžou zaráz změnit desítky atributů a nechceme spouštět vyhodnocení práv pro každý.
     */
    protected override async __evaluateRightsOnAttributeChanged(): Promise<void> {
        if (!this.actualObject.isClientSideEncrypted && this.actualObject.classInfo.encryptingAllowed) {
            await (this._newEvaluateRights([...RightGroups.getControlRightGroupTypes(ControlRightTypesGroup.attributeChange), RightType.useEncryption, RightType.useDecryption, RightType.useDigitalSign]).catch(
                PromiseUtils.cancellationCatch));
        } else {
            await (this._newEvaluateRightsGroup(ControlRightTypesGroup.attributeChange).catch(
                PromiseUtils.cancellationCatch));
        }
    }
    /**
     * Aktualizuje data a workflow akce u stejného záznamu v seznamu
     * @param listObj vybraný objekt na seznamu
     */
    protected async _onDataRefreshed(listObj: OWObject): Promise<void> {
        if (this._workflowMenuItems && this._previousWorkflowMenuItems
            && !DataInterfaceComparer.arraysEquals(this._workflowMenuItems, this._previousWorkflowMenuItems)) {
            await listObj.reload();
            if (this.ownerForm.detailFormMasterFrame instanceof MasterableFrame) // TODO: už při vytváření se mělo jet přes rozhraní, grrr
                await this.ownerForm.detailFormMasterFrame.reloadWFItems();
        }
        this._previousWorkflowMenuItems = this._workflowMenuItems;
    }

    protected async _getWFItems(obj: OWObject): Promise<IWorkflowMenuItem[]> {
        if (this.hideButtons.workflow) {
            this._workflowMenuItems = null;
            return null;
        }

        let items: IWorkflowMenuItem[] = null;
        const wfItemsResult = await this._workflowManager.getWFItems(obj, true, this._getRequestContext()).catch((err) => {
            ErrorHandler.instance.handleError(err);
            return null;
        });
        if (wfItemsResult != null) {
            items = wfItemsResult.workflowMenuItems;
        }
        this._previousWorkflowMenuItems = this._workflowMenuItems;
        this._workflowMenuItems = items;

        return items;
    }

    /**
     * Znovu načte položky WF pro actualObject.
     */
    public async reloadWFItems(): Promise<void> {
        if (this.actualObject != null) {
            await this._getWFItems(this.actualObject);
        }
    }
    //#endregion Methods

    //#region IDisposable
    //destruktor
    public override dispose(): void {
        this.ownerForm.detailFormMasterFrame?.actualObjectChanged.remove(this._detailFormMasterFrameObjectChangedListener);
        this.contextCreated.remove(this._contextCreatedListener);

        this.rollbackContext();

        this.selectionChanged.removeAll();
        this.workflowItemsChanging.removeAll();
        this.workflowItemsChanged.removeAll();

        super.dispose();
    }

    //#endregion IDisposable
}
